import { useState, useEffect } from "react";
import { API } from "../App";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Beaker, Loader2, ChevronRight, ArrowLeft, CheckCircle2, XCircle,
  AlertTriangle, BarChart3, BookOpen, Send, Clock, Trophy
} from "lucide-react";

const CATEGORY_LABELS = {
  statistics: "Statistics",
  measurement_systems: "Measurement Systems",
  reliability: "Reliability",
  medical_device: "Medical Device",
  quality: "Quality",
  risk_management: "Risk Management",
  audit_leadership: "Audit Leadership",
};

const DIFFICULTY_COLORS = {
  intermediate: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  advanced: "bg-red-500/10 text-red-400 border-red-500/20",
};

const LabsPage = () => {
  const { t } = useTranslation('platform');
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLab, setSelectedLab] = useState(null);
  const [labDetail, setLabDetail] = useState(null);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [filterCat, setFilterCat] = useState("");

  useEffect(() => {
    const params = {};
    if (filterCat) params.category = filterCat;
    Promise.all([
      axios.get(`${API}/labs/list`, { params }),
      axios.get(`${API}/labs/history`, { withCredentials: true }).catch(() => ({ data: { submissions: [] } })),
    ]).then(([labsRes, histRes]) => {
      setLabs(labsRes.data.labs || []);
      setHistory(histRes.data.submissions || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [filterCat]);

  const openLab = async (labId) => {
    setSelectedLab(labId);
    setResults(null);
    setAnswers({});
    try {
      const res = await axios.get(`${API}/labs/detail/${labId}`);
      setLabDetail(res.data);
    } catch {}
  };

  const setAnswer = (qid, val) => {
    setAnswers(prev => ({ ...prev, [qid]: val }));
  };

  const submitLab = async () => {
    if (!labDetail) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/labs/submit/${labDetail.lab_id}`, { answers }, { withCredentials: true });
      setResults(res.data);
    } catch {}
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen bg-[#0A1628] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#1A6BCC] animate-spin" /></div>;

  /* ═══ LAB DETAIL + SUBMISSION ═══ */
  if (selectedLab && labDetail) {
    return (
      <div className="min-h-screen bg-[#0A1628] text-white" data-testid="lab-detail-view">
        <div className="sticky top-0 z-10 bg-[#0A1628]/95 backdrop-blur border-b border-[#1E3A5F]/50 px-6 py-3 flex items-center justify-between">
          <button onClick={() => { setSelectedLab(null); setLabDetail(null); setResults(null); }} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="back-to-labs">
            <ArrowLeft className="w-4 h-4" /> All Labs
          </button>
          <span className="text-xs text-slate-500 font-mono">{labDetail.lab_id}</span>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-[10px] px-2 py-0.5 rounded border ${DIFFICULTY_COLORS[labDetail.difficulty] || ""}`}>{labDetail.difficulty}</span>
              <span className="text-[10px] text-[#00C8E0]">{CATEGORY_LABELS[labDetail.category] || labDetail.category}</span>
            </div>
            <h1 className="text-2xl font-bold mb-2" data-testid="lab-title">{labDetail.title}</h1>
            <p className="text-sm text-slate-400">{labDetail.description}</p>
          </div>

          {/* Scenario */}
          <div className="bg-[#0D1F38] border border-[#1E3A5F]/50 rounded-lg p-5 mb-6">
            <h2 className="text-xs font-bold text-[#00C8E0] uppercase tracking-wider mb-3">Scenario</h2>
            <p className="text-sm text-slate-200 leading-relaxed" data-testid="lab-scenario">{labDetail.scenario}</p>
          </div>

          {/* Instructions */}
          <div className="mb-6">
            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Instructions</h2>
            <ol className="list-decimal list-inside space-y-1.5">
              {labDetail.instructions.map((inst, i) => (
                <li key={i} className="text-sm text-slate-300">{inst}</li>
              ))}
            </ol>
          </div>

          {/* Data */}
          <div className="bg-[#132340] border border-[#1E3A5F] rounded-lg p-5 mb-8">
            <h2 className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <BarChart3 className="w-3.5 h-3.5" /> Given Data
            </h2>
            <div className="text-xs font-mono text-slate-300 whitespace-pre-wrap" data-testid="lab-data">
              {Object.entries(labDetail.data).map(([key, val]) => {
                const display = Array.isArray(val) ? JSON.stringify(val) :
                  typeof val === "object" ? JSON.stringify(val, null, 2) : String(val);
                return (
                  <div key={key} className="mb-2">
                    <span className="text-[#00C8E0]">{key}:</span> {display}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Questions */}
          <div className="space-y-6 mb-8" data-testid="lab-questions">
            {labDetail.questions.map((q, qi) => {
              const result = results?.results?.find(r => r.question_id === q.id);
              return (
                <div key={q.id} className={`border rounded-lg p-5 ${result ? (result.correct ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5") : "border-[#1E3A5F] bg-[#0D1F38]"}`} data-testid={`question-${q.id}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-6 h-6 rounded-full bg-[#132340] flex items-center justify-center text-[10px] font-bold text-slate-400 shrink-0">{qi + 1}</div>
                    <p className="text-sm text-white flex-1">{q.text}</p>
                    {result && (result.correct ? <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" /> : <XCircle className="w-5 h-5 text-red-400 shrink-0" />)}
                  </div>

                  {q.type === "numeric" ? (
                    <input
                      type="number"
                      step="any"
                      value={answers[q.id] || ""}
                      onChange={(e) => setAnswer(q.id, e.target.value)}
                      disabled={!!results}
                      placeholder="Enter your answer..."
                      className="bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white w-full max-w-xs placeholder:text-slate-600 focus:border-[#1A6BCC] focus:outline-none disabled:opacity-60"
                      data-testid={`answer-${q.id}`}
                    />
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {q.options?.map((opt) => (
                        <button
                          key={opt}
                          onClick={() => !results && setAnswer(q.id, opt)}
                          disabled={!!results}
                          className={`px-3 py-1.5 rounded border text-xs transition-colors ${
                            answers[q.id] === opt
                              ? "border-[#1A6BCC] bg-[#1A6BCC]/20 text-white"
                              : "border-[#1E3A5F] bg-[#0A1628] text-slate-400 hover:border-[#1A6BCC]/40"
                          } disabled:opacity-60`}
                          data-testid={`option-${q.id}-${opt.substring(0, 10).replace(/\s/g, "_")}`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}

                  {result && !result.correct && (
                    <div className="mt-2 text-xs text-red-300">
                      Expected: <span className="font-mono text-white">{result.expected}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Submit / Results */}
          {!results ? (
            <button
              onClick={submitLab}
              disabled={submitting || Object.keys(answers).length === 0}
              className="w-full bg-[#1A6BCC] text-white py-3 rounded-lg text-sm font-bold hover:bg-[#1A6BCC]/80 transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
              data-testid="submit-lab"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Submit Answers
            </button>
          ) : (
            <div className={`border rounded-lg p-6 text-center ${results.grade === "Pass" ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"}`} data-testid="lab-results">
              <div className="flex items-center justify-center gap-2 mb-2">
                {results.grade === "Pass" ? <Trophy className="w-6 h-6 text-emerald-400" /> : <AlertTriangle className="w-6 h-6 text-red-400" />}
                <span className={`text-2xl font-bold ${results.grade === "Pass" ? "text-emerald-400" : "text-red-400"}`}>{results.score_pct}%</span>
              </div>
              <p className="text-sm text-white font-bold mb-1">{results.grade === "Pass" ? "Passed!" : "Not Yet — Review and Try Again"}</p>
              <p className="text-xs text-slate-400">{results.score}/{results.total} correct (70% required to pass)</p>
              <button
                onClick={() => { setResults(null); setAnswers({}); }}
                className="mt-4 px-6 py-2 bg-[#132340] border border-[#1E3A5F] rounded text-xs text-white hover:border-[#1A6BCC]/40"
                data-testid="retry-lab"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ═══ LAB LIST ═══ */
  const categories = [...new Set(labs.map(l => l.category))];
  const labHistory = {};
  history.forEach(h => {
    if (!labHistory[h.lab_id] || h.score_pct > labHistory[h.lab_id].score_pct) {
      labHistory[h.lab_id] = h;
    }
  });

  return (
    <div className="min-h-screen bg-[#0A1628] text-white" data-testid="labs-page">
      {/* Header */}
      <div className="border-b border-[#1E3A5F]/50 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-emerald-600 rounded flex items-center justify-center">
              <Beaker className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-sm">Lab Simulations</h1>
              <p className="text-[10px] text-slate-500">{labs.length} interactive exercises with auto-grading</p>
            </div>
          </div>
          <select
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            className="bg-[#0D1F38] border border-[#1E3A5F] rounded px-2 py-1 text-xs text-white focus:outline-none"
            data-testid="category-filter"
          >
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
          </select>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="labs-grid">
          {labs.map((lab) => {
            const best = labHistory[lab.lab_id];
            return (
              <button
                key={lab.lab_id}
                onClick={() => openLab(lab.lab_id)}
                className="text-left bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-5 hover:border-[#1A6BCC]/40 transition-colors group"
                data-testid={`lab-card-${lab.lab_id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-mono text-slate-500">{lab.lab_id}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded border ${DIFFICULTY_COLORS[lab.difficulty] || ""}`}>{lab.difficulty}</span>
                </div>
                <h3 className="text-sm font-bold text-white group-hover:text-[#00C8E0] transition-colors mb-2">{lab.title}</h3>
                <p className="text-xs text-slate-400 mb-3 line-clamp-2">{lab.description}</p>
                <div className="flex items-center justify-between text-[10px] text-slate-500">
                  <span>{CATEGORY_LABELS[lab.category] || lab.category} · {lab.question_count} questions</span>
                  {best && (
                    <span className={`font-bold ${best.grade === "Pass" ? "text-emerald-400" : "text-amber-400"}`}>
                      Best: {best.score_pct}%
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LabsPage;
